import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { useQuery } from "urql";
import dayjs from "dayjs";
import { Kpi as GqlKpi } from "@acto/gql/graphql";
import { KpiComparisonMode, StandardPeriod } from "@acto/taskpane/core/domain";
import { toGqlKpiComparisonMode, toGqlPeriod } from "./kpiTransformers";

const CustomerKpiFiguresQuery = graphql(`
  query GetCustomerKpiFigures(
    $id: ID!
    $period: StandardPeriod
    $dateRange: DateRangeInput
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: 1, filter: { id: $id }) {
      __typename
      edges {
        __typename
        node {
          id
          __typename
          kpiSummaries(
            period: $period
            dateRange: $dateRange
            granularity: MONTHLY
            kpis: [REVENUE, PROFIT, ORDERS]
            comparisonMode: $comparisonMode
          ) {
            values {
              kpi
              totalValue
              change {
                percentageChange
              }
              comparison {
                totalValue
                range {
                  startDate
                  endDate
                }
                change {
                  percentageChange
                }
              }
              timeSeries {
                dataPoints {
                  periodStart
                  value
                }
              }
            }
          }
          meanDaysBetweenOrders(period: $period, dateRange: $dateRange) {
            value
          }
        }
      }
    }
  }
`);

export type CustomerFigureKpi = "REVENUE" | "PROFIT" | "ORDERS";

export interface MonthlyBreakdown {
  month: string;
  value: number;
  change: number | null;
  periodStart: string;
}

export interface KpiFigures {
  labels: string[];
  values: number[];
  totalValue: number;
  percentageChange: number;
  monthlyBreakdown: MonthlyBreakdown[];
  previousTotalValue: number | null;
  previousRange: { startDate: string; endDate: string } | null;
}

export interface CustomerKpiFigures {
  byKpi: Partial<Record<CustomerFigureKpi, KpiFigures>>;
  meanDaysBetweenOrders: number | null;
  isFetching: boolean;
  isPending: boolean;
}

interface UseCustomerKpiFiguresOptions {
  clientId: string;
  period: StandardPeriod;
  dateRange?: { startDate: string; endDate: string };
  comparisonMode: KpiComparisonMode;
}

const FIGURE_KPIS: Record<GqlKpi, CustomerFigureKpi | undefined> = {
  [GqlKpi.Revenue]: "REVENUE",
  [GqlKpi.Profit]: "PROFIT",
  [GqlKpi.Orders]: "ORDERS",
  [GqlKpi.Margin]: undefined,
  [GqlKpi.IncomingOrder]: undefined,
};

/**
 * Month-over-month change in percent, rounded to one decimal; null for the first month and where
 * the prior month is zero.
 */
function calculateMonthlyChanges(dataPoints: { periodStart: string; value: number }[]): MonthlyBreakdown[] {
  return dataPoints.map((point, index) => {
    const previousValue = index > 0 ? dataPoints[index - 1].value : null;
    const change = previousValue ? ((point.value - previousValue) / previousValue) * 100 : null;
    return {
      month: dayjs(point.periodStart).format("MMMM YYYY"),
      value: point.value,
      change: change !== null ? Math.round(change * 10) / 10 : null,
      periodStart: point.periodStart,
    };
  });
}

export function useCustomerKpiFigures({
  clientId,
  period,
  dateRange,
  comparisonMode,
}: UseCustomerKpiFiguresOptions): CustomerKpiFigures {
  const [result] = useQuery({
    query: CustomerKpiFiguresQuery,
    variables: {
      id: clientId,
      period: toGqlPeriod(period),
      dateRange,
      comparisonMode: toGqlKpiComparisonMode(comparisonMode),
    },
    pause: !clientId,
  });

  const client = result.data?.clients.edges[0]?.node;

  const byKpi = useMemo(() => {
    const figures: Partial<Record<CustomerFigureKpi, KpiFigures>> = {};
    for (const value of client?.kpiSummaries?.values ?? []) {
      const kpi = FIGURE_KPIS[value.kpi];
      const dataPoints = value.timeSeries.dataPoints;
      if (!kpi || dataPoints.length === 0) continue;
      figures[kpi] = {
        labels: dataPoints.map((point) => dayjs(point.periodStart).format("MMM")),
        values: dataPoints.map((point) => Math.round(point.value)),
        totalValue: value.totalValue,
        percentageChange: value.comparison?.change.percentageChange ?? value.change.percentageChange ?? 0,
        monthlyBreakdown: calculateMonthlyChanges(dataPoints),
        previousTotalValue: value.comparison?.totalValue ?? null,
        previousRange: value.comparison?.range ?? null,
      };
    }
    return figures;
  }, [client]);

  const meanDays = client?.meanDaysBetweenOrders?.value;

  return {
    byKpi,
    meanDaysBetweenOrders: meanDays != null && !Number.isNaN(meanDays) ? meanDays : null,
    isFetching: result.fetching || result.stale,
    isPending: result.fetching && !result.data,
  };
}
